import { useLazyQuery, useQuery } from "@apollo/client";
import {
  GetOrderDesignByIdDocument,
  GetOrderDesignFilesDocument,
} from "../../gql/generated/graphql";
import { useCallback, useMemo } from "react";
import { type OrderDesign } from "@jasper/client-models";
import { formatOrderDesign } from "./utils";

interface Props {
  orderDesignId: string;
}

interface UseOrderDesignVisualizationHookResult {
  orderDesign: OrderDesign | null;
  isLoading: boolean;
  getOrderDesignFiles?: () => Promise<string[]>;
  error?: Error;
}

export const useOrderDesignVisualization = ({
  orderDesignId,
}: Props): UseOrderDesignVisualizationHookResult => {
  const { data, loading, error } = useQuery(GetOrderDesignByIdDocument, {
    variables: { orderDesignId },
  });

  const [getOrderDesignFilesQuery] = useLazyQuery(GetOrderDesignFilesDocument);

  const orderDesign = useMemo((): OrderDesign | null => {
    if (!data?.getOrderDesign) {
      return null;
    }

    return formatOrderDesign(data?.getOrderDesign);
  }, [data]);

  const getOrderDesignFiles = useCallback(async () => {
    if (!orderDesign?.id) {
      return [] as string[];
    }

    const { data, error } = await getOrderDesignFilesQuery({
      variables: { orderDesignId: orderDesign.id },
    });

    if (error) {
      throw error;
    }

    return data?.getOrderDesignFiles ?? ([] as string[]);
  }, [orderDesign, getOrderDesignFilesQuery]);

  return {
    getOrderDesignFiles,
    orderDesign,
    isLoading: loading,
    error,
  };
};
